import { useAppConfig } from "~/app/config/useAppConfig";
import { type ReactNode, useCallback, useEffect, useState } from "react";
import CartContainsItemsModal from "~/features/orders/orderDetails/components/AddToCart/CartContainsItemsModal";
import DeleteCartConfirmationModal from "~/domains/cart/components/DeleteCartConfirmationModal";
import Modal from "~/common/components/Modal";
import Spinner from "~/common/components/Spinner/Spinner";
import { useDispatch, useSelector } from "react-redux";
import { addProductsFromOrder, loadSelectedProductData } from "~/features/cart/cart/redux/actions/orderDetailsActions";

import { Button, Stack } from "~/common/mui";
import { selectIsCartEmpty } from "~/features/addToCart/redux/cartAmounts/cartAmountSelectors";
import type { RootState } from "~/app/store";
import { CART_PAGE } from "~/utils/url/staticUrls";
import type { OrderLineItemData } from "~/domains/orders/types/modelsV2";
import { useTranslation } from "react-i18next";
import { openModal } from "~/common/utils/ModalService";
import CartSuccessModal from "~/features/addToCart/components/AddToCartProcess/Modals/CartSuccessModal/CartSuccessModal";
import { Alert } from "~/common/mui/Alert/Alert";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";

interface Props {
  onClose: () => void;
  selectedOrderItems: OrderLineItemData[];
}

enum Step {
  LOADING,
  HAS_CART,
  CONFIRM_OVERRIDE,
  ADDING_DATA,
  SUCCESS,
  ERROR,
}

const OrderDetailsAddToCartFlow = ({ onClose, selectedOrderItems }: Props) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const webservicesDisabled = useAppConfig()?.configuration.disableAllWS ?? false;
  const isCartLoaded = useSelector((state: RootState) => !state.cartPage.loading && !state.cartPage.error);
  const isCartEmpty = useSelector(selectIsCartEmpty);

  const [step, setStep] = useState<Step>(isCartLoaded && !isCartEmpty ? Step.HAS_CART : Step.LOADING);

  const onMount = useCallback(() => {
    dispatch(loadSelectedProductData(selectedOrderItems));
  }, [dispatch, selectedOrderItems]);

  const onAdd = useCallback(
    (override?: boolean) => dispatch(addProductsFromOrder(selectedOrderItems, override)),
    [dispatch, selectedOrderItems],
  );

  const addCb = useCallback(
    async (override?: boolean) => {
      setStep(Step.ADDING_DATA);
      try {
        const { responseItems } = await onAdd(override);
        openModal("add-to-cart-dialog", (close) => (
          <CartSuccessModal onClose={close} page="ORDER_DETAILS" responseItems={responseItems} />
        ));
        onClose();
      } catch (e) {
        console.log(e);
        setStep(Step.ERROR);
      }
    },
    [onAdd, onClose],
  );

  useEffect(() => {
    if (step === Step.LOADING && isCartLoaded) {
      if (isCartEmpty) {
        addCb(false);
      } else {
        setStep(Step.HAS_CART);
      }
    }
  });

  useEffect(onMount, []);

  const successCount = selectedOrderItems.map((item) => item.qty).reduce((acc, qty) => acc + qty);
  const cartPageUrl = CART_PAGE;

  return (
    <>
      {step === Step.HAS_CART && (
        <CartContainsItemsModal
          onCancel={onClose}
          onOverride={() => setStep(Step.CONFIRM_OVERRIDE)}
          onAdd={() => addCb(false)}
        />
      )}
      {step === Step.CONFIRM_OVERRIDE && (
        <DeleteCartConfirmationModal onCancel={() => setStep(Step.HAS_CART)} onOk={() => addCb(true)} />
      )}
      {(step === Step.LOADING || step === Step.ADDING_DATA) && (
        <Modal onClose={onClose}>
          <Spinner className={"mb-30"} />
        </Modal>
      )}
      {step === Step.SUCCESS && (
        <Modal
          onClose={onClose}
          footerChildren={
            <Stack direction={"row"} gap={1}>
              {getCloseBtn(t("close"), onClose)}
              <div data-test-id={"orders_addtocart_gotocart"}>
                {getActionBtn(t("addToCartPopup.checkoutBtn"), cartPageUrl, undefined, webservicesDisabled)}
              </div>
            </Stack>
          }
        >
          <Alert className="mb-30" severity={"info"}>
            {t("wishlistPage.globalMsg.addedToCart", { count: successCount })}
          </Alert>
        </Modal>
      )}
      {step === Step.ERROR && (
        <Modal
          onClose={onClose}
          footerChildren={
            <Stack direction={"row"} gap={1}>
              {getCloseBtn(t("close"), onClose)}
              {getActionBtn(t("addToCartPopup.checkoutBtn"), cartPageUrl, true, webservicesDisabled)}
            </Stack>
          }
        >
          <Alert className="mb-30" severity={"error"}>
            {t(`addToCartPopup.add.error`)}
          </Alert>
        </Modal>
      )}
    </>
  );
};

export default OrderDetailsAddToCartFlow;

function getCloseBtn(label: ReactNode, onClose: () => void) {
  return <Button onClick={onClose}>{label}</Button>;
}

type Func = () => void;
function getActionBtn(label: ReactNode, action: string | Func, disabled?: boolean, webservicesDisabled?: boolean) {
  const props = {
    disabled: (webservicesDisabled ?? false) || disabled,
    href: typeof action === "string" ? action : undefined,
    onClick: typeof action === "function" ? action : undefined,
  };
  return (
    <Button startIcon={<ShoppingCartIcon sx={{ fontSize: { phone: 16, tablet: 20 } }} />} {...props}>
      {label}
    </Button>
  );
}
